import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  ShieldCheck, KeyRound, ChevronDown, ChevronUp, Loader2,
  ShieldAlert, LifeBuoy, Lock, ArrowRight, Copy, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { LogoLockup } from "../components/Logo";
import {
  adminMe,
  adminRecoveryUse, adminRecoveryVerify,
  adminTotpVerify,
  adminTotpBootstrapSetup, adminTotpBootstrapEnable,
} from "../lib/api";

const OP_TOKEN_KEY = "archive-admin-token";
const SESSION_KEY = "archive_session_token";

// Build-time flag. Defaults to OFF — the legacy operator-token field is NOT
// rendered on the public production console. To re-enable in preview/dev,
// set REACT_APP_ADMIN_SHOW_LEGACY_TOKEN=true in the appropriate .env.
const SHOW_LEGACY_OPERATOR_TOKEN =
  String(process.env.REACT_APP_ADMIN_SHOW_LEGACY_TOKEN || "").toLowerCase() === "true";

const Shell = ({ children, badge = "RESTRICTED — EDITORIAL CONSOLE", icon = <ShieldCheck size={16} /> }) => (
  <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[#0a0907]" data-testid="admin-login-page">
    <div className="w-full max-w-md border border-[#2D2A26] bg-[#131313] p-8 aged-paper">
      <div className="flex items-center gap-2 mb-5 text-[#D92D20]">
        {icon}
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">// {badge}</span>
      </div>
      {children}
    </div>
  </div>
);

const IdleLockBanner = () => (
  <div
    className="mb-4 border border-[#D92D20] bg-[#1a0808] px-3 py-2 flex items-center gap-2"
    role="status"
    data-testid="admin-idle-lock-banner"
  >
    <ShieldAlert size={14} className="text-[#D92D20] shrink-0" />
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D92D20]">
      Session locked due to inactivity.
    </span>
  </div>
);

const SessionExpiredBanner = () => (
  <div
    className="mb-4 border border-[#CBB892] bg-[#1a1408] px-3 py-2 flex items-center gap-2"
    role="status"
    data-testid="admin-session-expired-banner"
  >
    <ShieldAlert size={14} className="text-[#CBB892] shrink-0" />
    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#CBB892]">
      Session expired — sign in again.
    </span>
  </div>
);

const LockoutBanner = ({ seconds }) => {
  const [remaining, setRemaining] = useState(seconds || 0);
  useEffect(() => {
    if (remaining <= 0) return;
    const t = setInterval(() => setRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [remaining]);
  if (remaining <= 0) return null;
  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  const h = Math.floor(m / 60);
  const label = h > 0
    ? `${h}h ${m % 60}m`
    : m > 0 ? `${m}m ${s}s` : `${s}s`;
  return (
    <div className="border border-[#D92D20]/60 bg-[#1a0808] px-4 py-3 mb-5" data-testid="lockout-banner">
      <div className="flex items-center gap-2 mb-1 text-[#D92D20]">
        <Lock size={13} />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">// LOCKED OUT</span>
      </div>
      <p className="text-sm text-[#FFA9A0] leading-relaxed">
        Too many incorrect verification attempts. For security, this code has
        been invalidated. Please try again in <span className="font-mono">{label}</span>.
      </p>
    </div>
  );
};

const AdminLogin = () => {
  const { login, logout, user, token: userToken, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const intended =
    loc.state?.from ||
    (typeof window !== "undefined" && sessionStorage.getItem("archive_admin_intended")) ||
    "/admin/submissions";

  // Navigate to the originally requested admin page and clear the
  // cross-OAuth-roundtrip memory of it.
  const goIntended = useCallback(() => {
    try { sessionStorage.removeItem("archive_admin_intended"); } catch { /* noop */ }
    nav(intended, { replace: true });
  }, [intended, nav]);

  // Start Google OAuth. Router state does not survive the full-page
  // redirect, so persist BOTH the post-login return path (consumed by
  // AuthCallback — without it the user lands on the public homepage)
  // and the intended admin destination in sessionStorage.
  const startGoogleLogin = () => {
    try {
      sessionStorage.setItem("archive_post_login_path", "/admin/login");
      sessionStorage.setItem("archive_admin_intended", intended);
    } catch { /* noop */ }
    login("/admin/login");
  };

  // Hidden / unadvertised paths into the recovery flow:
  //   /admin/login?recover=1     (linkable, e.g. from a bookmark you keep)
  //   /admin/login#recover       (URL fragment, won't survive logs as easily)
  const recoveryHintRequested =
    params.get("recover") === "1" ||
    (typeof window !== "undefined" && window.location.hash === "#recover");

  // Surface a top banner when we got here because of admin idle-lock.
  const idleLocked = params.get("reason") === "idle_locked";
  // Session-expired can arrive via the URL (?reason=session_expired from the
  // admin route guard) OR via the one-shot sessionStorage breadcrumb left by
  // AuthContext when it purges a dead token before the guard can run.
  const [expiredBreadcrumb] = useState(() => {
    try {
      const v = sessionStorage.getItem("archive_login_reason");
      if (v) sessionStorage.removeItem("archive_login_reason");
      return v === "session_expired";
    } catch { return false; }
  });
  const sessionExpired =
    params.get("reason") === "session_expired" || expiredBreadcrumb;

  // steps: loading | google | totp | totp-enroll | enroll-success | forbidden | recovery-code | recovery-otp
  const [step, setStep] = useState("loading");
  const [showToken, setShowToken] = useState(false);
  const [opToken, setOpToken] = useState("");
  const [busy, setBusy] = useState(false);

  const [otpExpiresIn, setOtpExpiresIn] = useState(0);
  const [otpCode, setOtpCode] = useState("");
  const [lockoutSeconds, setLockoutSeconds] = useState(0);

  // recovery state
  const [recoveryCode, setRecoveryCode] = useState("");
  const [recoveryOtp, setRecoveryOtp] = useState("");

  // TOTP enrollment state
  const [enrollData, setEnrollData] = useState(null); // {secret, otpauth_uri, account}
  const [enrollCode, setEnrollCode] = useState("");
  const [enrollBackupCodes, setEnrollBackupCodes] = useState(null);

  const reconcile = useCallback(async () => {
    // Wait until AuthContext hydrates the stored session — reconciling
    // before that fired a credential-less /admin/me probe and, right after
    // the OAuth redirect, could race the token write.
    if (authLoading) return;
    if (!userToken) {
      // No local session: show the Google step directly. Never send an
      // unauthenticated status probe — those were recorded as failed access
      // attempts and progressively locked the operator's own browser out.
      console.info("[admin-auth] no local session — showing Google sign-in (no probe)");
      setStep(recoveryHintRequested ? "recovery-code" : "google");
      return;
    }
    try {
      const me = await adminMe(null, userToken);
      if (me.is_admin) { goIntended(); return; }
      if (me.otp_required) {
        console.info("[admin-auth] google-verified — advancing to", me.method);
        if (me.method === "totp") setStep("totp");
        else if (me.method === "totp_enroll") setStep("totp-enroll");
        else setStep("totp-enroll"); // safety net — never fall back to email
        return;
      }
      // Allow direct linking into recovery via ?recover=1 even before sign-in.
      if (recoveryHintRequested) { setStep("recovery-code"); return; }
      setStep("google");
    } catch (e) {
      const code = e?.response?.status;
      console.info("[admin-auth] /admin/me rejected the session (status", code, ") — route guard falling back");
      if (code === 403) setStep("forbidden");
      else if (recoveryHintRequested) setStep("recovery-code");
      else setStep("google");
    }
  }, [userToken, authLoading, goIntended, recoveryHintRequested]);

  useEffect(() => { reconcile(); }, [reconcile]);

  useEffect(() => {
    if (otpExpiresIn <= 0) return;
    const t = setInterval(() => setOtpExpiresIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [otpExpiresIn]);

  // ---- handlers ----
  // (email OTP path removed — TOTP is the only second factor admitted)

  const verifyTotp = async () => {
    const c = otpCode.replace(/[^\d]/g, "");
    if (c.length !== 6 && c.length !== 8) {
      toast.error("Enter the 6-digit code from your app (or an 8-digit backup code)"); return;
    }
    setBusy(true);
    try {
      const r = await adminTotpVerify(userToken, c);
      if (r.used_backup) toast.success(`Verified via backup code (${r.backup_codes_remaining} left)`);
      else toast.success("Verified — admin access granted");
      goIntended();
    } catch (e) {
      const code = e?.response?.status;
      const data = e?.response?.data;
      if (code === 429 && data?.detail?.locked) {
        setLockoutSeconds(data.detail.seconds_remaining || 0); setOtpCode("");
      } else if (code === 401) toast.error("Incorrect code");
      else if (code === 400) toast.error(data?.detail || "Code rejected");
      else toast.error("Verification failed");
    } finally { setBusy(false); }
  };

  // ---- TOTP first-time enrollment (in-login bootstrap) ----
  // Fire the setup call exactly once when the user lands on the enroll step.
  const enrollSetupFiredRef = React.useRef(false);
  useEffect(() => {
    if (step !== "totp-enroll") { enrollSetupFiredRef.current = false; return; }
    if (enrollSetupFiredRef.current) return;
    if (enrollData) return;
    enrollSetupFiredRef.current = true;
    (async () => {
      setBusy(true);
      try {
        const r = await adminTotpBootstrapSetup(userToken);
        setEnrollData(r);
      } catch (e) {
        const code = e?.response?.status;
        if (code === 403) setStep("forbidden");
        else if (code === 409) setStep("totp"); // already enrolled — go to verify
        else if (code === 401) setStep("google");
        else toast.error(e?.response?.data?.detail || "Could not start TOTP setup");
        enrollSetupFiredRef.current = false;
      } finally { setBusy(false); }
    })();
  }, [step, userToken, enrollData]);

  const confirmEnroll = async () => {
    const c = enrollCode.replace(/\D/g, "");
    if (c.length !== 6) { toast.error("Enter the 6-digit code from your app"); return; }
    setBusy(true);
    try {
      const r = await adminTotpBootstrapEnable(userToken, c);
      setEnrollBackupCodes(r.backup_codes || []);
      setStep("enroll-success");
      toast.success("Authenticator app enabled");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) toast.error("Incorrect code. Check your authenticator app and retry.");
      else toast.error(e?.response?.data?.detail || "Could not verify code");
    } finally { setBusy(false); }
  };

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); }
    catch { /* noop */ }
  };

  const tryOperatorToken = async () => {
    if (!SHOW_LEGACY_OPERATOR_TOKEN) return; // belt + suspenders
    const t = opToken.trim();
    if (!t) return;
    setBusy(true);
    try {
      const me = await adminMe(t);
      if (me.is_admin) {
        sessionStorage.setItem(OP_TOKEN_KEY, t);
        toast.success("Operator access granted");
        goIntended();
      } else { toast.error("Token rejected"); }
    } catch (e) {
      const code = e?.response?.status;
      if (code === 429) toast.error("Too many failed attempts — try again later.");
      else toast.error("Invalid operator token");
    } finally { setBusy(false); }
  };

  const submitRecoveryCode = async () => {
    const c = recoveryCode.trim().toUpperCase();
    if (c.length < 20) { toast.error("Enter your full recovery code"); return; }
    setBusy(true);
    try {
      const r = await adminRecoveryUse(c);
      setOtpExpiresIn(r.expires_in_seconds || 600);
      toast.success("Recovery accepted — verification code sent to admin email");
      setStep("recovery-otp");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 401) toast.error("Invalid or already-used recovery code");
      else if (code === 429) toast.error("Too many attempts — try again later");
      else toast.error("Recovery failed");
    } finally { setBusy(false); }
  };

  const verifyRecoveryOtp = async () => {
    const c = recoveryOtp.replace(/\D/g, "");
    if (c.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    setBusy(true);
    try {
      const r = await adminRecoveryVerify(c);
      try { localStorage.setItem(SESSION_KEY, r.session_token); } catch { /* noop */ }
      toast.success("Recovery verified — admin access granted");
      try { sessionStorage.removeItem("archive_admin_intended"); } catch { /* noop */ }
      window.location.assign(intended);
    } catch (e) {
      const code = e?.response?.status;
      const data = e?.response?.data;
      if (code === 429 && data?.detail?.locked) {
        setLockoutSeconds(data.detail.seconds_remaining || 0);
        setRecoveryOtp("");
      } else if (code === 400) toast.error(data?.detail || "Code rejected");
      else toast.error("Verification failed");
    } finally { setBusy(false); }
  };

  // ---- render ----
  if (step === "loading") return <Shell><div className="flex items-center gap-2 text-[#A39E93]"><Loader2 size={14} className="animate-spin" /> Checking session…</div>{idleLocked && <div className="mt-3"><IdleLockBanner /></div>}{sessionExpired && <div className="mt-3"><SessionExpiredBanner /></div>}</Shell>;

  if (step === "forbidden") return (
    <Shell badge="403 — NOT AUTHORIZED" icon={<ShieldAlert size={16} />}>
      <h1 className="font-display text-3xl text-[#E4DFD3] mb-3">Access denied</h1>
      <p className="text-sm text-[#A39E93] mb-6">
        Signed in as <span className="text-[#E4DFD3]">{user?.email || "(unknown)"}</span>. This
        account is not on the admin allowlist.
      </p>
      <div className="flex gap-2">
        <button onClick={async () => { await logout(); setStep("google"); }} className="flex-1 border border-[#454038] hover:border-[#D92D20] text-[#E4DFD3] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
          Sign out
        </button>
        <a href="/" className="flex-1 text-center border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
          Back to archive
        </a>
      </div>
    </Shell>
  );

  if (step === "totp") {
    return (
      <Shell badge="STEP 2 OF 2 — AUTHENTICATOR APP">
        <h1 className="font-display text-3xl text-[#E4DFD3] mb-2">Enter verification code</h1>
        <p className="text-sm text-[#A39E93] mb-5 leading-relaxed">
          Open your authenticator app (Google Authenticator, Authy, 1Password, etc.)
          and enter the 6-digit code for <span className="text-[#E4DFD3]">The Archive</span>.
          You can also use an 8-digit backup code.
        </p>
        <LockoutBanner seconds={lockoutSeconds} key={lockoutSeconds} />
        <input
          type="text" inputMode="numeric" maxLength={8} autoFocus
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value.replace(/[^\d]/g, "").slice(0, 8))}
          onKeyDown={(e) => { if (e.key === "Enter" && !lockoutSeconds) verifyTotp(); }}
          placeholder="000000"
          disabled={lockoutSeconds > 0}
          data-testid="admin-totp-input"
          className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-3 font-mono text-2xl tracking-[0.4em] text-center text-[#E4DFD3] placeholder:text-[#454038] disabled:opacity-40"
        />
        <button type="button" onClick={verifyTotp}
          disabled={busy || (otpCode.length !== 6 && otpCode.length !== 8) || lockoutSeconds > 0}
          data-testid="admin-totp-submit"
          className="mt-3 w-full flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em]">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
          Verify & Unlock
        </button>
        <button type="button" onClick={async () => { await logout(); setStep("google"); }}
          className="mt-6 w-full text-center font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] hover:text-[#A39E93]">
          Cancel — sign out
        </button>
      </Shell>
    );
  }

  if (step === "totp-enroll") {
    return (
      <Shell badge="STEP 2 OF 2 — SET UP AUTHENTICATOR" icon={<KeyRound size={16} />}>
        <h1 className="font-display text-3xl text-[#E4DFD3] mb-2">Enrol an authenticator</h1>
        <p className="text-sm text-[#A39E93] mb-5 leading-relaxed">
          This account is allowlisted but has no second factor yet. Scan the QR
          (or paste the secret) into an authenticator app — Google Authenticator,
          Authy, 1Password — then enter the 6-digit code it shows to finish setup
          and unlock the console.
        </p>

        {!enrollData && (
          <div className="flex items-center gap-2 text-[#A39E93] py-6">
            <Loader2 size={14} className="animate-spin" /> Generating secret…
          </div>
        )}

        {enrollData && (
          <>
            <div className="border border-[#2D2A26] bg-[#0a0a0a] p-4 mb-4 flex items-start gap-4">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(enrollData.otpauth_uri)}&bgcolor=0a0a0a&color=e4dfd3&margin=4`}
                alt="TOTP QR code"
                data-testid="enroll-qr"
                width={140}
                height={140}
                className="border border-[#2D2A26] shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">// MANUAL KEY</div>
                <div className="flex items-center gap-2">
                  <code
                    data-testid="enroll-secret"
                    className="flex-1 font-mono text-xs tracking-[0.12em] bg-[#0a0a0a] border border-[#2D2A26] px-2 py-1.5 text-[#E4DFD3] break-all"
                  >
                    {enrollData.secret}
                  </code>
                  <button
                    type="button"
                    onClick={() => copyToClipboard(enrollData.secret)}
                    className="border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-2 py-1.5"
                    title="Copy secret"
                  >
                    <Copy size={11} />
                  </button>
                </div>
                <div className="mt-2 font-mono text-[9px] text-[#757065]">
                  Account: <span className="text-[#A39E93]">{enrollData.account}</span><br />
                  Issuer: <span className="text-[#A39E93]">{enrollData.issuer}</span>
                </div>
              </div>
            </div>

            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">
              // ENTER THE 6-DIGIT CODE
            </div>
            <input
              type="text" inputMode="numeric" maxLength={6} autoFocus
              value={enrollCode}
              onChange={(e) => setEnrollCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => { if (e.key === "Enter") confirmEnroll(); }}
              placeholder="000000"
              data-testid="enroll-code-input"
              className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-3 font-mono text-2xl tracking-[0.4em] text-center text-[#E4DFD3] placeholder:text-[#454038]"
            />
            <button
              type="button"
              onClick={confirmEnroll}
              disabled={busy || enrollCode.length !== 6}
              data-testid="enroll-confirm-btn"
              className="mt-3 w-full flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em]"
            >
              {busy ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
              Verify & enable
            </button>
          </>
        )}

        <button type="button" onClick={async () => { await logout(); setStep("google"); }}
          className="mt-6 w-full text-center font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] hover:text-[#A39E93]">
          Cancel — sign out
        </button>
      </Shell>
    );
  }

  if (step === "enroll-success" && enrollBackupCodes) {
    return (
      <Shell badge="STORE THESE CODES — SHOWN ONCE" icon={<AlertCircle size={16} />}>
        <div className="flex items-center gap-2 mb-3 text-[#5DD4B3]">
          <ShieldCheck size={14} />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">
            // AUTHENTICATOR ENABLED
          </span>
        </div>
        <h1 className="font-display text-3xl text-[#E4DFD3] mb-2">Save your backup codes</h1>
        <p className="text-sm text-[#A39E93] mb-5 leading-relaxed">
          These 8 single-use recovery codes can be used in place of your authenticator
          app code if you lose access to the device. <span className="text-[#E4DFD3]">Store them
          offline now — they will never be shown again.</span> A password manager or a
          printed copy in a safe both work.
        </p>
        <div
          data-testid="enroll-backup-codes"
          className="grid grid-cols-2 gap-2 mb-4 border border-[#5DD4B3]/40 bg-[#0a1612] p-3"
        >
          {enrollBackupCodes.map((c) => (
            <code
              key={c}
              className="font-mono text-sm tracking-[0.12em] bg-[#0a0a0a] border border-[#2D2A26] px-3 py-2 text-[#E4DFD3] text-center"
            >
              {c}
            </code>
          ))}
        </div>
        <div className="flex gap-2 mb-5">
          <button
            type="button"
            onClick={() => copyToClipboard(enrollBackupCodes.join("\n"))}
            className="flex-1 inline-flex items-center justify-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] hover:text-[#E4DFD3] px-3 py-2 font-mono text-[9px] uppercase tracking-[0.25em]"
          >
            <Copy size={11} /> Copy all
          </button>
        </div>
        <button
          type="button"
          onClick={() => { setEnrollBackupCodes(null); setEnrollData(null); setEnrollCode(""); goIntended(); }}
          data-testid="enroll-continue-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          I&apos;ve saved them — continue to console <ArrowRight size={12} />
        </button>
      </Shell>
    );
  }

  if (step === "recovery-otp") {
    const mins = Math.floor(otpExpiresIn / 60);
    const secs = otpExpiresIn % 60;
    return (
      <Shell badge="EMERGENCY RECOVERY — STEP 2">
        <h1 className="font-display text-3xl text-[#E4DFD3] mb-2">Enter verification code</h1>
        <p className="text-sm text-[#A39E93] mb-5 leading-relaxed">
          A 6-digit code was generated for your admin email by the recovery flow.
        </p>
        <LockoutBanner seconds={lockoutSeconds} key={lockoutSeconds} />
        <input
          type="text" inputMode="numeric" maxLength={6} autoFocus
          value={recoveryOtp}
          onChange={(e) => setRecoveryOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={(e) => { if (e.key === "Enter" && !lockoutSeconds) verifyRecoveryOtp(); }}
          placeholder="000000"
          disabled={lockoutSeconds > 0}
          data-testid="admin-otp-input"
          className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-3 font-mono text-2xl tracking-[0.4em] text-center text-[#E4DFD3] placeholder:text-[#454038] disabled:opacity-40"
        />
        <button
          type="button" onClick={verifyRecoveryOtp}
          disabled={busy || recoveryOtp.length !== 6 || lockoutSeconds > 0}
          data-testid="admin-otp-submit"
          className="mt-3 w-full flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          {busy ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
          Verify & Unlock
        </button>
        <div className="flex items-center justify-between mt-4 text-[#757065]">
          <span className="font-mono text-[9px] uppercase tracking-[0.25em]">
            {otpExpiresIn > 0
              ? `Expires in ${String(mins).padStart(2,"0")}:${String(secs).padStart(2,"0")}`
              : (lockoutSeconds > 0 ? "Locked" : "Code expired")}
          </span>
        </div>
        <button type="button" onClick={() => { setStep("recovery-code"); setRecoveryOtp(""); }}
          className="mt-6 w-full text-center font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] hover:text-[#A39E93]">
          Cancel recovery
        </button>
      </Shell>
    );
  }

  if (step === "recovery-code") return (
    <Shell badge="EMERGENCY RECOVERY — STEP 1" icon={<LifeBuoy size={16} />}>
      <h1 className="font-display text-3xl text-[#E4DFD3] mb-2">Emergency access</h1>
      <p className="text-sm text-[#A39E93] mb-5 leading-relaxed">
        Enter your single-use recovery code. We&apos;ll email a verification code to
        the admin address. This will burn this recovery code permanently.
      </p>
      <input
        type="text" autoFocus
        value={recoveryCode}
        onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
        onKeyDown={(e) => { if (e.key === "Enter") submitRecoveryCode(); }}
        placeholder="XXXXX-XXXXX-XXXXX-XXXXX"
        data-testid="admin-recovery-input"
        className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-3 font-mono text-sm tracking-[0.2em] text-center text-[#E4DFD3] placeholder:text-[#454038]"
      />
      <button
        type="button" onClick={submitRecoveryCode}
        disabled={busy || recoveryCode.trim().length < 20}
        data-testid="admin-recovery-submit"
        className="mt-3 w-full flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-3 font-mono text-[10px] uppercase tracking-[0.25em]"
      >
        {busy ? <Loader2 size={11} className="animate-spin" /> : <LifeBuoy size={11} />}
        Send recovery code
      </button>
      <button type="button" onClick={() => setStep("google")}
        className="mt-6 w-full text-center font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] hover:text-[#A39E93]">
        Back to sign in
      </button>
    </Shell>
  );

  // step === "google" — the only path advertised on the public production page.
  // Fully branded "THE ARCHIVE — Restricted Editorial Console" page.
  // (The hosted Emergent/Google OAuth pages themselves are not customisable,
  //  so this is the last branded surface before the redirect; we use it to
  //  set the tone clearly.)
  const FILE_NO = "2026 / ACCESS-TIER-1";
  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0a0907]" data-testid="admin-login-page">
      {/* Subtle grid + film grain background to match the rest of the site */}
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(217,45,32,0.07), transparent 55%)" }} />

      {/* Top utility bar — file metadata */}
      <div className="relative z-10 border-b border-[#1f1c18] bg-black/40 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-3 flex items-center justify-between gap-4">
          <LogoLockup markSize={32} />
          <div className="hidden md:flex items-center gap-6 font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">
            <span>FILE NO. {FILE_NO}</span>
            <span className="flex items-center gap-1.5 text-[#D92D20]">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#D92D20] animate-pulse" />
              SECURE CHANNEL
            </span>
          </div>
        </div>
      </div>

      <main className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-20 grid md:grid-cols-[1.1fr_0.9fr] gap-10 md:gap-16 items-start">
        {/* LEFT — the "case file" panel */}
        <section className="relative">
          {idleLocked && <IdleLockBanner />}
          {sessionExpired && <SessionExpiredBanner />}
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#D92D20] mb-4 flex items-center gap-3">
            <span>// RESTRICTED — INTERNAL USE ONLY</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-[#E4DFD3] leading-[1.05] mb-5">
            Restricted Editorial Console
          </h1>
          <p className="text-[#A39E93] text-base md:text-lg leading-relaxed max-w-xl mb-6">
            This console controls dossier moderation, audience records, and
            audit trails for <span className="text-[#E4DFD3]">The Archive</span>.
            Sign in with the allowlisted Google admin account, then complete
            verification.
          </p>

          {/* Three-step indicator */}
          <ol className="space-y-3 mb-8">
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex w-6 h-6 items-center justify-center border border-[#D92D20] text-[#D92D20] font-mono text-[10px]">1</span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E4DFD3]">Continue with Google</div>
                <div className="text-xs text-[#757065] mt-0.5">Only allowlisted admin accounts can proceed.</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex w-6 h-6 items-center justify-center border border-[#454038] text-[#757065] font-mono text-[10px]">2</span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">Authenticator code</div>
                <div className="text-xs text-[#757065] mt-0.5">6-digit TOTP from your authenticator app.</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex w-6 h-6 items-center justify-center border border-[#454038] text-[#757065] font-mono text-[10px]">3</span>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">Console access</div>
                <div className="text-xs text-[#757065] mt-0.5">Sessions are short-lived and fully audited.</div>
              </div>
            </li>
          </ol>

          {/* Warning callout */}
          <div className="border border-[#454038] bg-[#0F0F0F]/60 px-4 py-3 max-w-xl">
            <div className="flex items-center gap-2 mb-1 text-[#CBB892]">
              <ShieldAlert size={12} />
              <span className="font-mono text-[10px] uppercase tracking-[0.3em]">// HEADS UP</span>
            </div>
            <p className="text-xs text-[#A39E93] leading-relaxed">
              You&apos;ll briefly leave The Archive while Google verifies you.
              The Google sign-in screen is not branded — that&apos;s expected.
              You&apos;ll be brought back here for step 2.
            </p>
          </div>
        </section>

        {/* RIGHT — the "manila folder" sign-in card */}
        <section className="md:sticky md:top-20">
          <div className="border border-[#2D2A26] bg-[#131313] aged-paper p-7 md:p-8 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D92D20]/60 to-transparent" />
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2 text-[#D92D20]">
                <ShieldCheck size={14} />
                <span className="font-mono text-[10px] uppercase tracking-[0.3em]">// ACCESS POINT</span>
              </div>
              <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
                ARCHIVE / ADMIN
              </span>
            </div>

            <h2 className="font-display text-2xl text-[#E4DFD3] mb-2">Sign in to The Archive</h2>
            <p className="text-sm text-[#A39E93] mb-6 leading-relaxed">
              The single sign-in path for the editorial console. All other doors
              are sealed.
            </p>

            <button
              type="button"
              onClick={startGoogleLogin}
              data-testid="admin-google-signin"
              className="group w-full inline-flex items-center justify-between gap-3 bg-[#E4DFD3] hover:bg-white text-[#0F0F0F] px-5 py-4 font-mono text-[11px] uppercase tracking-[0.3em] transition-colors"
            >
              <span className="flex items-center gap-3">
                {/* Google G mark — inline SVG, neutral */}
                <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  <path fill="none" d="M0 0h48v48H0z"/>
                </svg>
                Continue with Google
              </span>
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </button>

            <div className="mt-5 border-t border-dashed border-[#2D2A26] pt-4 space-y-2 font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
              <div className="flex items-center gap-2">
                <Lock size={10} /> All access is logged
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck size={10} /> Allowlist + TOTP 2FA enforced
              </div>
              <div className="flex items-center gap-2">
                <ShieldAlert size={10} /> Non-admin accounts will be rejected
              </div>
            </div>

            {SHOW_LEGACY_OPERATOR_TOKEN && (
              <div className="mt-6 border-t border-[#2D2A26] pt-4">
                <button type="button" onClick={() => setShowToken((s) => !s)}
                  data-testid="admin-token-toggle"
                  className="w-full flex items-center justify-center gap-2 text-[#757065] hover:text-[#A39E93] font-mono text-[9px] uppercase tracking-[0.25em]">
                  {showToken ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                  Operator token (legacy — preview only)
                </button>
                {showToken && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-2 text-[#757065]">
                      <KeyRound size={12} />
                      <span className="font-mono text-[9px] uppercase tracking-[0.3em]">// LEGACY — PREVIEW/DEV ONLY</span>
                    </div>
                    <p className="text-xs text-[#757065] mb-3 leading-relaxed">
                      Hidden in production. Backend rejects this path unless
                      <code className="text-[#A39E93]"> ADMIN_ALLOW_OPERATOR_TOKEN=true</code>.
                    </p>
                    <input type="password" value={opToken}
                      onChange={(e) => setOpToken(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") tryOperatorToken(); }}
                      placeholder="x-admin-token"
                      data-testid="admin-token-input"
                      className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2.5 font-mono text-sm text-[#E4DFD3] placeholder:text-[#757065]" />
                    <button type="button" onClick={tryOperatorToken}
                      disabled={busy || !opToken.trim()}
                      data-testid="admin-token-submit"
                      className="mt-2 w-full flex items-center justify-center gap-2 border border-[#454038] hover:border-[#D92D20] disabled:opacity-40 text-[#E4DFD3] px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em]">
                      {busy ? <Loader2 size={11} className="animate-spin" /> : null}
                      Authenticate
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <a
            href="/"
            className="mt-4 inline-flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] hover:text-[#E4DFD3]"
          >
            ← Back to the public archive
          </a>
        </section>
      </main>

      {/* Footer bar */}
      <div className="relative z-10 border-t border-[#1f1c18] bg-black/40 mt-12">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-3 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">
          <span>THE ARCHIVE · HIDDEN TRUTHS · EST. 2026</span>
          <span>ALL ACCESS LOGGED</span>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
